import {
  EDITOR_EMAIL,
  FIREBASE_API_KEY,
  FIREBASE_DATABASE_URL,
} from "./config";
import { products as defaultProducts } from "./products";
import { cloneDefaultSiteContent } from "./siteContent";

const WEBSITE_URL = `${FIREBASE_DATABASE_URL}/website.json`;
const SIGN_IN_URL =
  `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${FIREBASE_API_KEY}`;
const LOOKUP_URL =
  `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${FIREBASE_API_KEY}`;

const mergeContent = (defaults, saved) => {
  if (Array.isArray(defaults)) return Array.isArray(saved) ? saved : defaults;
  if (!defaults || typeof defaults !== "object") return saved ?? defaults;
  const source = saved && typeof saved === "object" ? saved : {};
  return Object.keys(defaults).reduce(
    (result, key) => ({ ...result, [key]: mergeContent(defaults[key], source[key]) }),
    { ...source }
  );
};

const normalizeBrandCopy = (value) => {
  if (typeof value === "string") {
    return value.replace(/EVLuxe|EvLuxe|EVLUXE/g, "evLUXE");
  }
  if (Array.isArray(value)) return value.map(normalizeBrandCopy);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, normalizeBrandCopy(entry)])
    );
  }
  return value;
};

const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
};

const readError = async (response) => {
  try {
    const body = await response.json();
    const code = body?.error?.message || body?.error || body?.detail;
    if (["INVALID_LOGIN_CREDENTIALS", "INVALID_PASSWORD", "EMAIL_NOT_FOUND"].includes(code)) {
      return "Incorrect editor password.";
    }
    if (code === "PERMISSION_DENIED" || response.status === 401) {
      return "Your editor session expired. Sign out and sign in again.";
    }
    if (code === "CONFIGURATION_NOT_FOUND") {
      return "Editor sign-in is not configured yet.";
    }
    return code || "The website service could not complete this request.";
  } catch {
    return "The website service could not complete this request.";
  }
};

export const loadProductCatalog = async () => {
  const website = await loadWebsiteCatalog();
  return {
    products: website.products,
    initialized: website.productsInitialized,
    updatedAt: website.updatedAt,
    connected: website.connected,
    error: website.error,
  };
};

export const loadWebsiteCatalog = async () => {
  try {
    const response = await fetchWithTimeout(WEBSITE_URL, { cache: "no-store" });
    if (!response.ok) throw new Error(await readError(response));
    const website = (await response.json()) || {};
    const productsInitialized = Array.isArray(website.products);
    const contentInitialized = Boolean(
      website.content && typeof website.content === "object" && !Array.isArray(website.content)
    );
    return {
      products: normalizeBrandCopy(productsInitialized ? website.products : defaultProducts),
      content: normalizeBrandCopy(
        contentInitialized
          ? mergeContent(cloneDefaultSiteContent(), website.content)
          : cloneDefaultSiteContent()
      ),
      productsInitialized,
      contentInitialized,
      updatedAt: website.updatedAt || null,
      connected: true,
    };
  } catch (error) {
    return {
      products: normalizeBrandCopy(defaultProducts),
      content: normalizeBrandCopy(cloneDefaultSiteContent()),
      productsInitialized: false,
      contentInitialized: false,
      updatedAt: null,
      connected: false,
      error: error.message,
    };
  }
};

export const verifyEditorPassword = async (password) => {
  const response = await fetchWithTimeout(SIGN_IN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: EDITOR_EMAIL,
      password,
      returnSecureToken: true,
    }),
  });
  if (!response.ok) throw new Error(await readError(response));
  const session = await response.json();
  return { authenticated: true, credential: session.idToken };
};

export const verifyEditorCredential = async (credential) => {
  const response = await fetchWithTimeout(LOOKUP_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken: credential }),
  });
  if (!response.ok) throw new Error(await readError(response));
  const result = await response.json();
  if (result?.users?.[0]?.email !== EDITOR_EMAIL) {
    throw new Error("This account is not allowed to use the editor.");
  }
  return { authenticated: true };
};

export const publishProductCatalog = async (products, credential) => {
  const current = await loadWebsiteCatalog();
  return publishWebsiteCatalog(products, current.content, credential);
};

export const publishWebsiteCatalog = async (products, content, credential) => {
  const response = await fetchWithTimeout(
    `${WEBSITE_URL}?auth=${encodeURIComponent(credential)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ products, content, updatedAt: new Date().toISOString() }),
    }
  );
  if (!response.ok) throw new Error(await readError(response));
  const website = await response.json();
  return {
    products: normalizeBrandCopy(website.products),
    content: normalizeBrandCopy(mergeContent(cloneDefaultSiteContent(), website.content)),
    updatedAt: website.updatedAt,
  };
};
